import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import dynamic from 'next/dynamic';

import type { KeyedMutator } from 'swr';

// Hooks
import useUser from '@/utils/hooks/swr/useUser';

import useLabelsAlbumsOnMedia from '@/utils/hooks/utils/useLabelsAlbumsOnMedia';

// Lib
import { POST, SQUARE, STORY } from '@/lib/posts/postTypes';

type MediaType = {
    id: string;
    labels: {
        label: string;
    }[];
    albums: {
        id: number;
        count_medias: number;
        name: string;
        type: 'auto' | 'manual';
    }[];
};

type RequestParams = {
    sort?: { column?: string };
    [key: string]: unknown;
} | null;

// Components
import ApplyGlobalStoreDates from '@/components/pages/myprofile/ApplyGlobalStoreDates';
import ErrorPostsListFallback from '@/components/pages/myprofile/ErrorPostsListFallback';
import FiltersSide from '@/components/pages/myprofile/filters/FiltersSide';
import PostsList from '@/components/pages/myprofile/PostsList';
const ExportXlsPostsModal = dynamic(
    () => import('@/components/pages/myprofile/modals/ExportXlsPostsModal')
);

type PostsTabProps = {
    isFiltersOpen?: boolean;
    toggleFilters?: () => void;
    isExportModalOpen?: boolean;
    toggleExportModal?: () => void;
    networkType?: string;
    /** @deprecated Use networkType instead */
    socialNetworkType?: string;
    /** @deprecated Use networkType instead */
    activeSocialProfile?: string;
    dataMedias?: Record<'medias' | 'stories', MediaType[]>[];
    isLoadMore?: boolean;
    isLoadingMore?: boolean;
    isLoadingInitialData?: boolean;
    requestParams?: RequestParams;
    setRequestParams?: (params: RequestParams | ((prev: RequestParams) => RequestParams)) => void;
    mutateMedias?: KeyedMutator<Record<'medias' | 'stories', MediaType[]>[]>;
    loadMoreMedia?: () => void;
    postFormat?: string;
    postType?: string;
    exportType?: string;
    renderFilters?: (
        resetFilters: boolean,
        setResetFilters: (value: boolean) => void
    ) => React.ReactNode;
    header?: (
        setRequestParams: PostsTabProps['setRequestParams'],
        postType?: string
    ) => React.ReactNode;
};

const PostsTab = ({
    isFiltersOpen,
    toggleFilters,
    isExportModalOpen,
    toggleExportModal,
    networkType,
    socialNetworkType,
    activeSocialProfile,
    dataMedias,
    isLoadMore,
    isLoadingMore,
    isLoadingInitialData,
    requestParams,
    setRequestParams,
    mutateMedias,
    loadMoreMedia,
    postFormat = SQUARE,
    postType = POST,
    exportType = POST,
    renderFilters,
    header
}: PostsTabProps) => {
    const { user } = useUser();
    const [resetFilters, setResetFilters] = useState(false);

    const { handleAlbumsEvent, handleLabelsEvent } = useLabelsAlbumsOnMedia({
        mutateMedias,
        requestParams: requestParams as { filter_labels?: string[] } | undefined,
        type: postType !== STORY ? 'medias' : 'stories'
    });

    return (
        <>
            {setRequestParams && (
                // @ts-expect-error - ApplyGlobalStoreDates returns void but is a valid side-effect component
                <ApplyGlobalStoreDates setRequestParams={setRequestParams} />
            )}
            <ExportXlsPostsModal
                isOpen={isExportModalOpen}
                toggle={toggleExportModal}
                requestParams={requestParams}
                postType={postType}
                exportType={exportType}
            />

            <div className="z-0 flex flex-1 flex-col @xl/wrapper-platform-content:flex-row">
                <div className="flex flex-1 flex-col">
                    {header ? header(setRequestParams, postType) : null}
                    <ErrorBoundary
                        FallbackComponent={ErrorPostsListFallback}
                        onReset={() => mutateMedias()}>
                        <PostsList
                            type={postType !== STORY ? 'medias' : 'stories'}
                            configPost={{
                                postFormat: postFormat,
                                postType: postType,
                                activeFilter: requestParams?.sort?.column ?? null,
                                network: networkType ?? socialNetworkType ?? activeSocialProfile,
                                numberFormat: user?.settings.numbers_format
                            }}
                            displayDates={
                                requestParams && requestParams?.sort?.column === 'created'
                            }
                            mutateMedias={mutateMedias}
                            isLoadMore={isLoadMore}
                            isLoadingMore={isLoadingMore}
                            loadMoreMedia={loadMoreMedia}
                            dataMedias={dataMedias}
                            requestParamsMedias={requestParams}
                            isFiltersOpen={isFiltersOpen}
                            isLoadingInitialData={isLoadingInitialData}
                            handleLabelsEvent={handleLabelsEvent}
                            handleAlbumsEvent={handleAlbumsEvent}
                        />
                    </ErrorBoundary>
                </div>
                {renderFilters ? (
                    <FiltersSide
                        isOpen={isFiltersOpen}
                        onResetFilters={() => setResetFilters(true)}
                        toggleFilters={toggleFilters}
                        className="">
                        {renderFilters(resetFilters, setResetFilters)}
                    </FiltersSide>
                ) : null}
            </div>
        </>
    );
};

export default PostsTab;
